import React, { useCallback, useMemo, useState } from 'react';
import { createContext } from '../dissolve/utils';
import { Offer, Product } from '../types';
import { getCart } from '../api/shared-app-requests';
import { logError } from '../utils/logging';

function productOfferNotParentProduct(offer: Offer, parentProduct?: Product) {
  if (offer.hide_upsell_product_already_in_cart) {
    return offer.shopify_product_handle !== parentProduct?.handle;
  }
  return true;
}

function productOfferNotInCart(
  offer: Offer,
  productsInCart: Array<string | number>,
) {
  if (offer.hide_upsell_product_already_in_cart) {
    let isInCart = false;
    if (offer.shopify_product_handle) {
      isInCart = productsInCart.includes(offer.shopify_product_handle);
    }
    if (offer.shopify_product_variant_id) {
      isInCart =
        productsInCart.includes(offer.shopify_product_variant_id) || isInCart;
    }

    return !isInCart;
  }
  return true;
}

export interface CandyRackContextShape {
  parentProduct?: Product;
  setParentProduct: (product: Product) => void;
  parentProductVariantId?: number;
  setParentProductVariantId: (id: number) => void;
  resetParentProductVariantId: () => void;
  quantity: number;
  setQuantity: (quantity: number) => void;
  upgradeOffer?: Offer;
  setUpgradeOffer: (offer: Offer) => void;
  resetUpgradeOffer: () => void;
  removeOffersWithProductAlreadyInCart: (offers: Offer[]) => Promise<Offer[]>;
}

const [useCandyRackContext, ContextProvider, ContextConsumer] =
  createContext<CandyRackContextShape>();

interface Props {
  children: React.ReactNode;
}

const CandyRackContextProvider = ({ children }: Props) => {
  const [parentProduct, setParentProduct] = useState<Product>();
  const [parentProductVariantId, setParentProductVariantId] =
    useState<number>();
  const [quantity, setQuantity] = useState(0);
  const [upgradeOffer, setUpgradeOffer] = useState<Offer>();

  const resetParentProductVariantId = () => {
    setParentProductVariantId(undefined);
  };

  const resetUpgradeOffer = () => {
    setUpgradeOffer(undefined);
  };

  const removeOffersWithProductAlreadyInCart = useCallback(
    async (offers: Offer[]) => {
      try {
        if (
          !offers.some((offer) => offer.hide_upsell_product_already_in_cart)
        ) {
          return offers;
        }
        const cartResponse = await getCart();
        const productsInCart = cartResponse.items.flatMap((item: any) => [
          item.handle,
          item.variant_id,
        ]);
        return offers.filter(
          (offer) =>
            productOfferNotInCart(offer, productsInCart) &&
            productOfferNotParentProduct(offer, parentProduct),
        );
      } catch (error) {
        logError('Failed to load cart', error);
        return offers;
      }
    },
    [parentProduct],
  );

  const providerValue: CandyRackContextShape = useMemo(
    () => ({
      parentProduct,
      setParentProduct,
      parentProductVariantId,
      setParentProductVariantId,
      resetParentProductVariantId,
      quantity,
      setQuantity,
      upgradeOffer,
      setUpgradeOffer,
      resetUpgradeOffer,
      removeOffersWithProductAlreadyInCart,
    }),
    [
      parentProduct,
      parentProductVariantId,
      quantity,
      upgradeOffer,
      removeOffersWithProductAlreadyInCart,
    ],
  );

  return <ContextProvider value={providerValue}>{children}</ContextProvider>;
};

const CandyRackContextConsumer =
  ContextConsumer as React.Consumer<CandyRackContextShape>;

export {
  useCandyRackContext,
  CandyRackContextProvider,
  CandyRackContextConsumer,
};
